import { useCallback, useEffect, useState } from "react";
import {
  adjustAdminUserSparks,
  banAdminUser,
  deleteAdminPost,
  deleteAdminParallel,
  deleteAllAdminParallels,
  deleteAllAdminPosts,
  deleteAdminUser,
  fetchAdminActivity,
  fetchAdminCampaigns,
  fetchAdminDashboard,
  fetchAdminPosts,
  fetchAdminPurgeOrphans,
  fetchAdminReports,
  fetchAdminUsers,
  fetchAdmobConfig,
  mediaUrl,
  reviewAdminCampaign,
  reviewAdminReport,
  updateAdminPost,
  updateAdminUser,
  updateAdmobConfig,
  type AdminActivity,
  type AdminCampaign,
  type AdminPost,
  type AdminReport,
  type AdminStats,
  type AdminUser,
  type AdmobConfig,
} from "@/api/admin";
import { AdminDataTab } from "@/components/admin/AdminDataTab";
import { AdminDashboardTab, AdminFinanceTab } from "@/components/admin/AdminDashboardTab";
import { AdminEconomyTab } from "@/components/admin/AdminEconomyTab";
import { AdminBusyOverlay, AdminToastStack, useAdminToasts } from "@/components/admin/AdminFeedback";
import { AdminMediaPreview } from "@/components/admin/AdminMediaPreview";
import { AdminParallelsTab } from "@/components/admin/AdminParallelsTab";
import { AdminPlatformTab } from "@/components/admin/AdminPlatformTab";
import { AdminRewardsTab } from "@/components/admin/AdminRewardsTab";
import { AdminShell, AdminCard } from "@/components/admin/AdminShell";
import { downloadCsv, formatInr, ADMIN_LOGIN_PATH } from "@/components/admin/adminConstants";
import type { AdminTab } from "@/components/admin/adminConstants";
import { useNoIndex } from "@/lib/noIndex";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";

function AvatarThumb({ url }: { url: string | null | undefined }) {
  const src = mediaUrl(url);
  if (!src) {
    return <div className="h-10 w-10 shrink-0 rounded-full bg-[#f0f0f0]" />;
  }
  return (
    <img src={src} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover bg-[#f0f0f0]" />
  );
}

function PostRow({
  post,
  previewVariant,
  busy,
  removing,
  showVideoControls,
  onEdit,
  onToggle,
  onDelete,
}: {
  post: AdminPost;
  previewVariant: "image" | "video";
  busy: boolean;
  removing?: boolean;
  showVideoControls?: boolean;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <li
      className={[
        "rounded-2xl border border-black/[0.06] bg-white p-4 shadow-sm transition",
        removing ? "admin-row-exit" : "",
      ].join(" ")}
    >
      <div className="flex gap-3">
        <AdminMediaPreview
          post={post}
          tall={previewVariant === "video"}
          variant={previewVariant}
          controls={showVideoControls}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="font-medium">
                @{post.username} ·{" "}
                {previewVariant === "video" ? "Drift" : post.is_parallel ? "Parallel" : post.type} ·{" "}
                {post.status}
              </div>
              <div className="text-xs text-ink-muted">
                {post.moderation_status} · {post.vibe_count ?? 0} vibes · {post.comment_count ?? 0}{" "}
                comments · {new Date(post.created_at).toLocaleString()}
              </div>
              <p className="mt-2 line-clamp-2 text-sm">{post.caption || "(no caption)"}</p>
            </div>
            <div className="flex flex-wrap gap-1">
              <Button variant="outline" onClick={onEdit}>
                Edit
              </Button>
              <Button variant="outline" disabled={busy} onClick={onToggle}>
                {post.moderation_status === "approved" ? "Disable" : "Enable"}
              </Button>
              <Button disabled={busy} onClick={onDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

export function AdminPage() {
  useNoIndex();
  const { signOut } = useAuth();
  const { toasts, push, dismiss } = useAdminToasts();
  const [tab, setTab] = useState<AdminTab>("dashboard");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("Saving…");
  const [removingPostIds, setRemovingPostIds] = useState<Set<string>>(new Set());
  const [purgeNote, setPurgeNote] = useState<string | null>(null);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [posts, setPosts] = useState<AdminPost[]>([]);
  const [drifts, setDrifts] = useState<AdminPost[]>([]);
  const [reports, setReports] = useState<AdminReport[]>([]);
  const [campaigns, setCampaigns] = useState<AdminCampaign[]>([]);
  const [activity, setActivity] = useState<AdminActivity[]>([]);
  const [config, setConfig] = useState<AdmobConfig>({});
  const [userSearch, setUserSearch] = useState("");
  const [postSearch, setPostSearch] = useState("");
  const [driftSearch, setDriftSearch] = useState("");
  const [parallelSearch, setParallelSearch] = useState("");
  const [parallelStatus, setParallelStatus] = useState("");
  const [parallelRefresh, setParallelRefresh] = useState(0);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [reportType, setReportType] = useState("");
  const [reportStatus, setReportStatus] = useState("open");
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editingPost, setEditingPost] = useState<AdminPost | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      if (tab === "dashboard" || tab === "finance") setStats(await fetchAdminDashboard());
      else if (tab === "users") setUsers(await fetchAdminUsers(userSearch));
      else if (tab === "posts" || tab === "drifts" || tab === "parallels") {
        const purged = await fetchAdminPurgeOrphans();
        const total =
          (purged.parallel_sessions_deleted ?? 0) +
          (purged.posts_deleted ?? 0) +
          (purged.parallel_posts_deleted ?? 0);
        setPurgeNote(
          total > 0
            ? `Auto-removed ${total} orphan item(s) (pending parallels / no media).`
            : null,
        );
        if (tab === "posts") setPosts(await fetchAdminPosts(postSearch, { kind: "frame" }));
        else if (tab === "drifts") setDrifts(await fetchAdminPosts(driftSearch, { kind: "drift" }));
        else if (tab === "parallels") setParallelRefresh((n) => n + 1);
      } else if (tab === "reports") {
        setReports(await fetchAdminReports(reportType || undefined, reportStatus));
      } else if (tab === "promotions") {
        setCampaigns(await fetchAdminCampaigns(campaignFilter || undefined));
      } else if (tab === "ads") setConfig(await fetchAdmobConfig());
      else if (tab === "activity") setActivity(await fetchAdminActivity());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    }
  }, [tab, userSearch, postSearch, driftSearch, campaignFilter, reportType, reportStatus]);

  useEffect(() => {
    void fetchAdminDashboard().then(setStats).catch(() => undefined);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredReports = reports.filter((r) => {
    if (reportType === "drift") return r.report_type === "post" && r.is_drift;
    if (reportType === "post") return r.report_type === "post" && !r.is_drift;
    if (reportType) return r.report_type === reportType;
    return true;
  });

  async function runAction(
    fn: () => Promise<void>,
    options?: { label?: string; success?: string; errorPrefix?: string },
  ) {
    setBusy(true);
    setBusyLabel(options?.label ?? "Saving…");
    setError(null);
    try {
      await fn();
      await load();
      if (options?.success) push(options.success, "success");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Action failed";
      setError(options?.errorPrefix ? `${options.errorPrefix}: ${msg}` : msg);
      push(options?.errorPrefix ? `${options.errorPrefix}: ${msg}` : msg, "error");
    } finally {
      setBusy(false);
    }
  }

  async function removePost(post: AdminPost, label: string) {
    if (!window.confirm(`Delete this ${label}?`)) return;
    setRemovingPostIds((prev) => new Set(prev).add(post.id));
    await new Promise((r) => window.setTimeout(r, 320));
    await runAction(() => deleteAdminPost(post.id), {
      label: "Removing…",
      success: `${label} removed`,
      errorPrefix: "Delete failed",
    });
    setRemovingPostIds((prev) => {
      const next = new Set(prev);
      next.delete(post.id);
      return next;
    });
  }

  function toggleAd(key: keyof AdmobConfig) {
    const next = { ...config, [key]: !config[key] };
    setConfig(next);
    void runAction(() => updateAdmobConfig({ [key]: next[key] }));
  }

  return (
    <AdminShell
      tab={tab}
      onTabChange={setTab}
      reportBadge={stats?.reports?.total_open}
      onSignOut={() => void signOut().then(() => window.location.assign(ADMIN_LOGIN_PATH))}
    >
      <AdminBusyOverlay show={busy} label={busyLabel} />
      <AdminToastStack toasts={toasts} onDismiss={dismiss} />
      {purgeNote ? (
        <p className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 admin-toast-enter">
          {purgeNote}
        </p>
      ) : null}
      {error ? (
        <p className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      {tab === "dashboard" && stats ? <AdminDashboardTab stats={stats} /> : null}
      {tab === "data" ? (
        <AdminDataTab
          busy={busy}
          onBusy={(b, label) => {
            setBusy(b);
            if (label) setBusyLabel(label);
          }}
          onToast={(message, kind) => push(message, kind)}
        />
      ) : null}
      {tab === "platform" ? <AdminPlatformTab /> : null}
      {tab === "finance" && stats ? <AdminFinanceTab stats={stats} /> : null}
      {tab === "economy" ? <AdminEconomyTab /> : null}
      {tab === "rewards" ? <AdminRewardsTab /> : null}
      {tab === "parallels" ? (
        <AdminParallelsTab
          search={parallelSearch}
          status={parallelStatus}
          refreshKey={parallelRefresh}
          busy={busy}
          onSearch={() => void load()}
          onSearchChange={setParallelSearch}
          onStatusChange={setParallelStatus}
          onDelete={(sessionId) =>
            void runAction(() => deleteAdminParallel(sessionId), {
              label: "Removing parallel…",
              success: "Parallel session removed",
              errorPrefix: "Delete failed",
            })
          }
          onDeleteAll={() => {
            if (
              !window.confirm(
                "Delete ALL parallel sessions and their R2 media? This cannot be undone.",
              )
            ) {
              return;
            }
            void runAction(
              async () => {
                const result = await deleteAllAdminParallels();
                push(
                  `Deleted ${result.deleted ?? 0} parallel sessions (${result.r2_deleted ?? 0} R2 objects)`,
                  "success",
                );
              },
              { label: "Deleting all parallels…", errorPrefix: "Delete all failed" },
            );
          }}
        />
      ) : null}

        {tab === "users" ? (
          <AdminCard
            title="Users"
            action={
              <Button
                variant="outline"
                onClick={() =>
                  downloadCsv(
                    "daywia-users.csv",
                    ["username", "email", "posts", "drifts", "reports", "status"],
                    users.map((u) => [
                      u.username,
                      u.email,
                      u.post_count,
                      u.drift_count,
                      u.open_reports_against,
                      u.shadow_banned ? "banned" : "active",
                    ]),
                  )
                }
              >
                Export CSV
              </Button>
            }
          >
          <section className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <input
                className="min-w-[220px] flex-1 rounded-xl border border-black/10 bg-white px-3 py-2 text-sm"
                placeholder="Search username, name, email…"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
              <Button onClick={() => void load()} disabled={busy}>
                Search
              </Button>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-black/[0.06] bg-white">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-[#f8f9fb]">
                  <tr>
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">Stats</th>
                    <th className="px-3 py-2">Sparks / wallet</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-black/[0.06]">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <AvatarThumb url={u.avatar_url} />
                          <div>
                            <div className="font-medium">@{u.username ?? "—"}</div>
                            <div className="text-xs text-ink-muted">{u.full_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-muted">
                        {u.post_count ?? 0} posts · {u.drift_count ?? 0} drifts
                        {(u.open_reports_against ?? 0) > 0 ? (
                          <span className="ml-1 text-red-600">{u.open_reports_against} reports</span>
                        ) : null}
                        {u.verified_creator ? (
                          <span className="ml-1 text-primary">· verified</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-xs tabular-nums text-ink-muted">
                        {(u.sparks_balance ?? 0).toLocaleString()} sparks
                        <br />
                        {(u.wallet_balance_coins ?? 0).toLocaleString()} coins
                      </td>
                      <td className="px-3 py-2">{u.email ?? "—"}</td>
                      <td className="px-3 py-2">
                        {u.shadow_banned ? (
                          <span className="text-red-600">Banned</span>
                        ) : (
                          <span className="text-green-700">Active</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          <Button variant="outline" onClick={() => setEditingUser({ ...u })}>
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            disabled={busy}
                            onClick={() =>
                              void runAction(() =>
                                banAdminUser(u.id, !u.shadow_banned, "Admin action"),
                              )
                            }
                          >
                            {u.shadow_banned ? "Unban" : "Ban"}
                          </Button>
                          <Button
                            disabled={busy}
                            onClick={() => {
                              if (window.confirm(`Delete @${u.username ?? u.id}?`)) {
                                void runAction(() => deleteAdminUser(u.id));
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          </AdminCard>
        ) : null}

        {tab === "posts" ? (
          <AdminCard
            title="Frames"
            action={
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => {
                  if (
                    !window.confirm(
                      "Delete ALL frames (posts) and their R2 media? This cannot be undone.",
                    )
                  ) {
                    return;
                  }
                  void runAction(
                    async () => {
                      const result = await deleteAllAdminPosts("frame");
                      push(
                        `Deleted ${result.deleted ?? 0} frames (${result.r2_deleted ?? 0} R2 objects)`,
                        "success",
                      );
                    },
                    { label: "Deleting all frames…", errorPrefix: "Delete all failed" },
                  );
                }}
              >
                Delete all
              </Button>
            }
          >
          <section className="space-y-4">
            <SearchBar value={postSearch} onChange={setPostSearch} onSearch={() => void load()} busy={busy} />
            <ul className="space-y-3">
              {posts.map((p) => (
                <PostRow
                  key={p.id}
                  post={p}
                  previewVariant="image"
                  busy={busy}
                  removing={removingPostIds.has(p.id)}
                  onEdit={() => setEditingPost({ ...p })}
                  onToggle={() =>
                    void runAction(
                      () =>
                        updateAdminPost(p.id, {
                          moderation_status: p.moderation_status === "approved" ? "rejected" : "approved",
                        }),
                      {
                        label: "Updating…",
                        success: "Frame updated",
                      },
                    )
                  }
                  onDelete={() => void removePost(p, "frame")}
                />
              ))}
            </ul>
          </section>
          </AdminCard>
        ) : null}

        {tab === "drifts" ? (
          <AdminCard
            title="Drifts"
            action={
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => {
                  if (
                    !window.confirm(
                      "Delete ALL drifts and their R2 media? This cannot be undone.",
                    )
                  ) {
                    return;
                  }
                  void runAction(
                    async () => {
                      const result = await deleteAllAdminPosts("drift");
                      push(
                        `Deleted ${result.deleted ?? 0} drifts (${result.r2_deleted ?? 0} R2 objects)`,
                        "success",
                      );
                    },
                    { label: "Deleting all drifts…", errorPrefix: "Delete all failed" },
                  );
                }}
              >
                Delete all
              </Button>
            }
          >
          <section className="space-y-4">
            <SearchBar value={driftSearch} onChange={setDriftSearch} onSearch={() => void load()} busy={busy} />
            <ul className="space-y-3">
              {drifts.map((p) => (
                <PostRow
                  key={p.id}
                  post={p}
                  previewVariant="video"
                  showVideoControls
                  busy={busy}
                  removing={removingPostIds.has(p.id)}
                  onEdit={() => setEditingPost({ ...p })}
                  onToggle={() =>
                    void runAction(
                      () =>
                        updateAdminPost(p.id, {
                          moderation_status: p.moderation_status === "approved" ? "rejected" : "approved",
                        }),
                      {
                        label: "Updating…",
                        success: "Drift updated",
                      },
                    )
                  }
                  onDelete={() => void removePost(p, "drift")}
                />
              ))}
            </ul>
          </section>
          </AdminCard>
        ) : null}

        {tab === "reports" ? (
          <AdminCard title="Reports queue">
          <section className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <select
                className="rounded-xl border border-black/10 bg-white px-3 py-2 text-sm"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
              >
                <option value="">All types</option>
                <option value="post">Posts</option>
                <option value="drift">Drifts (posts)</option>
                <option value="comment">Comments</option>
                <option value="message">Messages</option>
                <option value="user">Users</option>
              </select>
              <select
                className="rounded-xl border border-black/10 bg-white px-3 py-2 text-sm"
                value={reportStatus}
                onChange={(e) => setReportStatus(e.target.value)}
              >
                <option value="open">Open</option>
                <option value="all">All</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
              </select>
              <Button onClick={() => void load()} disabled={busy}>
                Refresh
              </Button>
            </div>
            <ul className="space-y-3">
              {filteredReports.map((r) => (
                  <li
                    key={`${r.report_type}-${r.id}`}
                    className="rounded-2xl border border-black/[0.06] bg-white p-4 shadow-sm"
                  >
                    <div className="flex gap-3">
                      <AdminMediaPreview post={r} tall={r.is_drift} />
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-[#f3f4f6] px-2 py-0.5 text-xs font-medium uppercase">
                            {r.report_type}
                            {r.is_drift ? " · drift" : ""}
                          </span>
                          <span className="text-xs text-ink-muted">{r.status}</span>
                          <span className="text-xs text-ink-muted">
                            {new Date(r.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-sm font-medium">{r.reason}</div>
                        {r.details ? <p className="text-sm text-ink-muted">{r.details}</p> : null}
                        <p className="line-clamp-2 text-sm">{r.target_preview ?? "—"}</p>
                        <p className="text-xs text-ink-muted">
                          Reported by @{r.reporter_username} · Subject @{r.subject_username}
                        </p>
                        <div className="flex flex-wrap gap-1 pt-2">
                          <Button
                            variant="outline"
                            disabled={busy}
                            onClick={() =>
                              void runAction(() => reviewAdminReport(r.id, r.report_type, "dismiss"))
                            }
                          >
                            Dismiss
                          </Button>
                          {r.report_type !== "user" ? (
                            <Button
                              variant="outline"
                              disabled={busy}
                              onClick={() =>
                                void runAction(() =>
                                  reviewAdminReport(r.id, r.report_type, "remove_content"),
                                )
                              }
                            >
                              Remove content
                            </Button>
                          ) : null}
                          <Button
                            variant="outline"
                            disabled={busy}
                            onClick={() =>
                              void runAction(() =>
                                reviewAdminReport(r.id, r.report_type, "ban_author", r.reason),
                              )
                            }
                          >
                            Ban user
                          </Button>
                          <Button
                            disabled={busy}
                            onClick={() => {
                              if (window.confirm(`Delete @${r.subject_username}?`)) {
                                void runAction(() =>
                                  reviewAdminReport(r.id, r.report_type, "delete_user", r.reason),
                                );
                              }
                            }}
                          >
                            Delete user
                          </Button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          </section>
          </AdminCard>
        ) : null}

        {tab === "promotions" ? (
          <AdminCard title="Promotion campaigns">
          <section className="space-y-4">
            <select
              className="rounded-xl border border-black/10 bg-white px-3 py-2 text-sm"
              value={campaignFilter}
              onChange={(e) => setCampaignFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="pending_approval">Pending approval</option>
              <option value="active">Active</option>
              <option value="rejected">Rejected</option>
              <option value="suspended">Suspended</option>
            </select>
            <ul className="space-y-3">
              {campaigns.map((c) => (
                <li
                  key={c.id}
                  className="rounded-2xl border border-black/[0.06] bg-white p-4 shadow-sm"
                >
                  <div className="flex gap-3">
                    {c.target_thumbnail_url ? (
                      <img
                        src={mediaUrl(c.target_thumbnail_url) ?? ""}
                        alt=""
                        className="h-48 w-[108px] shrink-0 rounded-xl border border-black/[0.06] object-cover bg-[#f0f0f0]"
                      />
                    ) : null}
                    <div className="flex-1">
                      <div className="font-medium">
                        {c.campaign_type} · {c.package_label ?? "Package"} · {c.status}
                      </div>
                      <div className="text-sm text-ink-muted">
                        {c.business_name || c.username} · {c.views_purchased} views · {c.target_scope}
                        {c.budget_inr_paise ? ` · ${formatInr(c.budget_inr_paise)}` : ""}
                        {c.sparks_spent ? ` · ${c.sparks_spent} sparks` : ""}
                        {c.payment_method ? ` · ${c.payment_method}` : ""}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {c.status === "pending_approval" ? (
                          <>
                            <Button disabled={busy} onClick={() => void runAction(() => reviewAdminCampaign(c.id, "approve"))}>
                              Approve
                            </Button>
                            <Button variant="outline" disabled={busy} onClick={() => void runAction(() => reviewAdminCampaign(c.id, "reject"))}>
                              Reject
                            </Button>
                          </>
                        ) : null}
                        {c.status === "active" ? (
                          <Button variant="outline" disabled={busy} onClick={() => void runAction(() => reviewAdminCampaign(c.id, "suspend"))}>
                            Suspend
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
          </AdminCard>
        ) : null}

        {tab === "ads" ? (
          <AdminCard title="AdMob configuration" className="max-w-xl">
          <section className="space-y-4">
            {(
              [
                ["enabled", "All ads"],
                ["home_enabled", "Home feed"],
                ["drift_enabled", "Drift feed"],
                ["chat_enabled", "Chat inbox"],
                ["premium_ad_free", "Premium ad-free feature"],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={Boolean(config[key])} onChange={() => toggleAd(key)} disabled={busy} />
                {label}
              </label>
            ))}
            <Button
              disabled={busy}
              onClick={() =>
                void runAction(() =>
                  updateAdmobConfig({
                    home_native_interval: config.home_native_interval,
                    drift_native_interval: config.drift_native_interval,
                  }),
                )
              }
            >
              Save intervals
            </Button>
          </section>
          </AdminCard>
        ) : null}

        {tab === "activity" ? (
          <AdminCard title="Moderation activity">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#f8f9fb]">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Target</th>
                </tr>
              </thead>
              <tbody>
                {activity.map((a) => (
                  <tr key={`${a.source}-${a.id}`} className="border-t border-black/[0.06]">
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2">{a.source}</td>
                    <td className="px-3 py-2">{a.action}</td>
                    <td className="px-3 py-2">
                      {a.target_type} · {String(a.target_id).slice(0, 8)}…
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </AdminCard>
        ) : null}

      {editingUser ? (
        <Modal title="Edit user" onClose={() => setEditingUser(null)}>
          <UserEditForm
            user={editingUser}
            busy={busy}
            onChange={setEditingUser}
            onAdjustSparks={async (delta, reason) => {
              await adjustAdminUserSparks(editingUser.id, delta, reason);
              push(`Sparks adjusted by ${delta}`, "success");
              const refreshed = await fetchAdminUsers(userSearch);
              const updated = refreshed.find((u) => u.id === editingUser.id);
              if (updated) setEditingUser(updated);
            }}
            onSave={() =>
              void runAction(
                async () => {
                  await updateAdminUser(editingUser.id, {
                    username: editingUser.username,
                    full_name: editingUser.full_name,
                    bio: editingUser.bio,
                    website: editingUser.website,
                    avatar_url: editingUser.avatar_url,
                    is_private: editingUser.is_private,
                    is_business: editingUser.is_business,
                    business_name: editingUser.business_name,
                    verified_creator: editingUser.verified_creator,
                  });
                  setEditingUser(null);
                },
                { label: "Saving user…", success: "User profile saved" },
              )
            }
          />
        </Modal>
      ) : null}

      {editingPost ? (
        <Modal title="Edit post" onClose={() => setEditingPost(null)}>
          <PostEditForm
            post={editingPost}
            busy={busy}
            onChange={setEditingPost}
            onSave={() =>
              void runAction(
                async () => {
                  await updateAdminPost(editingPost.id, {
                    caption: editingPost.caption,
                    moderation_status: editingPost.moderation_status,
                    allow_comments: editingPost.allow_comments,
                    sensitive_content: editingPost.sensitive_content,
                  });
                  setEditingPost(null);
                },
                { label: "Saving post…", success: "Post saved" },
              )
            }
          />
        </Modal>
      ) : null}
    </AdminShell>
  );
}

function SearchBar({
  value,
  onChange,
  onSearch,
  busy,
}: {
  value: string;
  onChange: (v: string) => void;
  onSearch: () => void;
  busy: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <input
        className="min-w-[220px] flex-1 rounded-xl border border-black/10 bg-white px-3 py-2 text-sm"
        placeholder="Search caption, username, id…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <Button onClick={onSearch} disabled={busy}>
        Search
      </Button>
    </div>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="text-sm text-ink-muted">
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function UserEditForm({
  user,
  busy,
  onChange,
  onSave,
  onAdjustSparks,
}: {
  user: AdminUser;
  busy: boolean;
  onChange: (u: AdminUser) => void;
  onSave: () => void;
  onAdjustSparks: (delta: number, reason: string) => Promise<void>;
}) {
  const [sparksDelta, setSparksDelta] = useState("");
  const [sparksReason, setSparksReason] = useState("Admin adjustment");

  return (
    <div className="space-y-3 text-sm">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Username" value={user.username ?? ""} onChange={(v) => onChange({ ...user, username: v })} />
        <Field label="Full name" value={user.full_name ?? ""} onChange={(v) => onChange({ ...user, full_name: v })} />
        <Field label="Email (read-only)" value={user.email ?? ""} onChange={() => {}} readOnly />
        <Field
          label="Avatar URL"
          value={user.avatar_url ?? ""}
          onChange={(v) => onChange({ ...user, avatar_url: v })}
        />
        <Field label="Website" value={user.website ?? ""} onChange={(v) => onChange({ ...user, website: v })} />
        <Field
          label="Business name"
          value={user.business_name ?? ""}
          onChange={(v) => onChange({ ...user, business_name: v })}
        />
      </div>
      <Field label="Bio" value={user.bio ?? ""} onChange={(v) => onChange({ ...user, bio: v })} />
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={user.is_private}
            onChange={(e) => onChange({ ...user, is_private: e.target.checked })}
          />
          Private account
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={user.is_business}
            onChange={(e) => onChange({ ...user, is_business: e.target.checked })}
          />
          Business account
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={Boolean(user.verified_creator)}
            onChange={(e) => onChange({ ...user, verified_creator: e.target.checked })}
          />
          Verified creator
        </label>
      </div>
      <div className="rounded-xl bg-[#f8f9fb] p-3 text-xs">
        <div className="font-medium text-ink">Balances</div>
        <div className="mt-1 text-ink-muted">
          {(user.sparks_balance ?? 0).toLocaleString()} sparks ·{" "}
          {(user.wallet_balance_coins ?? 0).toLocaleString()} wallet coins
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="grow">
            Adjust sparks (+/-)
            <input
              type="number"
              className="mt-1 w-full rounded border border-black/10 bg-white px-2 py-1"
              value={sparksDelta}
              onChange={(e) => setSparksDelta(e.target.value)}
              placeholder="e.g. 50 or -10"
            />
          </label>
          <label className="min-w-[180px] grow">
            Reason
            <input
              className="mt-1 w-full rounded border border-black/10 bg-white px-2 py-1"
              value={sparksReason}
              onChange={(e) => setSparksReason(e.target.value)}
            />
          </label>
          <Button
            variant="outline"
            disabled={busy || !sparksDelta}
            onClick={() => {
              const delta = Number(sparksDelta);
              if (!delta) return;
              void onAdjustSparks(delta, sparksReason).then(() => setSparksDelta(""));
            }}
          >
            Apply sparks
          </Button>
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <Button onClick={onSave} disabled={busy}>
          Save profile
        </Button>
      </div>
    </div>
  );
}

function PostEditForm({
  post,
  busy,
  onChange,
  onSave,
}: {
  post: AdminPost;
  busy: boolean;
  onChange: (p: AdminPost) => void;
  onSave: () => void;
}) {
  return (
    <div className="space-y-3 text-sm">
      <AdminMediaPreview
        post={post}
        tall={post.is_drift}
        variant={post.is_drift ? "video" : "image"}
        controls={post.is_drift}
        className="!h-52 !w-[120px]"
      />
      <label className="block">
        Caption
        <textarea
          className="mt-1 w-full rounded border border-black/10 bg-white px-2 py-1"
          rows={4}
          value={post.caption}
          onChange={(e) => onChange({ ...post, caption: e.target.value })}
        />
      </label>
      <label className="block">
        Moderation
        <select
          className="mt-1 w-full rounded border border-black/10 bg-white px-2 py-1"
          value={post.moderation_status}
          onChange={(e) => onChange({ ...post, moderation_status: e.target.value })}
        >
          <option value="approved">approved</option>
          <option value="pending_review">pending_review</option>
          <option value="rejected">rejected</option>
        </select>
      </label>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={post.allow_comments}
            onChange={(e) => onChange({ ...post, allow_comments: e.target.checked })}
          />
          Allow comments
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={post.sensitive_content}
            onChange={(e) => onChange({ ...post, sensitive_content: e.target.checked })}
          />
          Sensitive content
        </label>
      </div>
      <Button onClick={onSave} disabled={busy} className={busy ? "" : "admin-save-pulse"}>
        {busy ? "Saving…" : "Save changes"}
      </Button>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  readOnly,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}) {
  return (
    <label className="block">
      {label}
      <input
        className="mt-1 w-full rounded border border-black/10 bg-white px-2 py-1 read-only:bg-[#f8f9fb]"
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
